import { createClient, type Session } from "@supabase/supabase-js";

let client: ReturnType<typeof createClient> | null = null;

export type PlatformUser = {
  id: string;
  email: string;
  phone_e164: string;
  display_name: string;
  role: "user" | "admin";
  status: "active" | "suspended" | "banned";
  status_reason: string;
  created_at: string;
  updated_at?: string;
};

export type PlatformActivity = {
  id: string;
  owner_id: string;
  title: string;
  description: string;
  city: string;
  location: string;
  starts_at: string;
  ends_at: string;
  capacity: number;
  allow_companions?: boolean;
  status: "draft" | "pending" | "approved" | "rejected" | "removed";
  moderation_reason: string;
  submitted_at: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type SensitiveWord = {
  id: string;
  phrase: string;
  severity: number;
  enabled: boolean;
};

export type PlatformAlert = {
  id: string;
  activity_id: string;
  matched_word_ids: string[];
  highest_severity: number;
  status: "open" | "acknowledged" | "resolved";
  acknowledged_by?: string | null;
  acknowledged_at?: string | null;
  created_at: string;
  activity_title?: string;
  activity_city?: string;
  activity_status?: string;
};

export type AuditLog = {
  id: number;
  actor_id: string;
  actor_name?: string;
  actor_email?: string;
  action: string;
  target_type: string;
  target_id: string;
  metadata: Record<string, unknown>;
  created_at: string;
};

export type AdSlot = {
  id: string;
  key: string;
  label: string;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
};

export type PlatformAd = {
  id: string;
  slot_id: string;
  slot_key?: string;
  slot_label?: string;
  title: string;
  body: string;
  image_url: string;
  target_url: string;
  starts_at: string | null;
  ends_at: string | null;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
};

export type AuthProviderSetting = {
  id: string;
  kind: "sms" | "oauth";
  provider_key: string;
  display_name: string;
  endpoint: string;
  template_id: string;
  signature: string;
  credential_ref: string;
  enabled: boolean;
  config?: Record<string, unknown>;
  template_type?: string;
};

// 后台只保存非机密配置：模板类型属于短信模板的用途，模板变量默认取 code。
export const SMS_TEMPLATE_TYPES = [
  { value: "verification", label: "验证码" },
  { value: "notification", label: "通知" },
  { value: "marketing", label: "营销" },
] as const;

export const ALIYUN_SMS_PRESET = {
  provider_key: "aliyun",
  display_name: "阿里云短信",
  endpoint: "https://dysmsapi.aliyuncs.com",
  template_id: "SMS_501605457",
  template_type: "verification",
  credential_ref: "ALIYUN_SMS_ACCESS_KEY",
} as const;

export function readTemplateType(provider: AuthProviderSetting): string {
  const fromConfig = provider.config?.template_type;
  if (typeof fromConfig === "string" && fromConfig) return fromConfig;
  return provider.template_type ?? "verification";
}

export async function sendTestSms(
  session: Session,
  phone: string,
  providerKey: string,
) {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-sms`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ phone, provider_key: providerKey }),
    },
  );
  const result = (await response.json().catch(() => ({}))) as {
    data?: { test_code?: string; template_code?: string };
    error?: string;
    code?: string;
  };
  if (!response.ok || result.error) {
    throw new Error(result.error || "测试短信发送失败");
  }
  return result.data ?? {};
}

export const getClient = () => {
  if (
    !import.meta.env.VITE_SUPABASE_URL ||
    !import.meta.env.VITE_SB_PUBLISHABLE_KEY
  ) {
    throw new Error("账号服务尚未配置");
  }
  client ??= createClient(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SB_PUBLISHABLE_KEY,
  );
  return client;
};

export async function callPlatformAdmin<T>(
  session: Session,
  action: string,
  payload: Record<string, unknown> = {},
) {
  let response: Response;
  try {
    response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/platform-admin`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action, ...payload }),
      },
    );
  } catch {
    throw new Error(
      "无法连接账号服务，请先启动本地 Supabase（54331）或检查网络配置",
    );
  }
  const result = (await response.json().catch(() => ({}))) as {
    data?: T;
    error?: string;
  };
  if (!response.ok || result.error) {
    // Do not retry rejected word writes through a different API: validation,
    // permissions and audit failures must remain visible to the administrator.
    if (action.includes("sensitive_word") && response.status !== 404) {
      throw new Error(result.error || "敏感词同步失败，请重试");
    }
    // Local Supabase does not automatically serve project functions. RLS on
    // platform_* tables provides the same server-side admin boundary while
    // functions are being served separately or deployed remotely.
    return callPlatformAdminDirect<T>(action, payload);
  }
  return result.data as T;
}

async function callPlatformAdminDirect<T>(
  action: string,
  payload: Record<string, unknown>,
): Promise<T> {
  const db: any = getClient();
  if (action === "overview") {
    const [
      { count: users },
      { count: activeUsers },
      { count: pendingActivities },
      { count: openAlerts },
      { count: backupUsers },
    ] = await Promise.all([
      db.from("platform_profiles").select("id", { count: "exact", head: true }),
      db
        .from("platform_profiles")
        .select("id", { count: "exact", head: true })
        .eq("status", "active"),
      db
        .from("platform_activities")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending"),
      db
        .from("platform_moderation_alerts")
        .select("id", { count: "exact", head: true })
        .eq("status", "open"),
      db.from("backup_status").select("owner_id", { count: "exact", head: true }),
    ]);
    return {
      users: users ?? 0,
      active_users: activeUsers ?? 0,
      pending_activities: pendingActivities ?? 0,
      open_alerts: openAlerts ?? 0,
      backup_users: backupUsers ?? 0,
    } as T;
  }
  if (action === "list_users") {
    let query = db
      .from("platform_profiles")
      .select(
        "id,display_name,role,status,status_reason,created_at,updated_at",
      )
      .order("created_at", { ascending: false })
      .limit(100);
    if (typeof payload.query === "string" && payload.query.trim()) {
      const q = String(payload.query).trim().replaceAll(",", "");
      query = query.ilike("display_name", `%${q}%`);
    }
    if (typeof payload.status === "string" && payload.status) query = query.eq("status", payload.status);
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []).map((row: Record<string, unknown>) => ({
      email: "",
      phone_e164: "",
      ...row,
    })) as T;
  }
  if (action === "set_user_status" || action === "set_user_role") {
    const fields =
      action === "set_user_status"
        ? { status: payload.status, status_reason: payload.reason ?? "" }
        : { role: payload.role };
    const { data, error } = await db
      .from("platform_profiles")
      .update(fields)
      .eq("id", payload.user_id)
      .select(
        "id,display_name,role,status,status_reason,created_at,updated_at",
      )
      .single();
    if (error) throw error;
    return { email: "", phone_e164: "", ...data } as T;
  }
  if (action === "list_activities") {
    let query = db
      .from("platform_activities")
      .select(
        "id,owner_id,title,description,city,location,starts_at,ends_at,capacity,allow_companions,status,moderation_reason,submitted_at,reviewed_by,reviewed_at,created_at,updated_at",
      )
      .order("submitted_at", { ascending: false, nullsFirst: false })
      .limit(100);
    if (payload.status) query = query.eq("status", payload.status);
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as T;
  }
  if (action === "create_official_activity") {
    const { data: identity } = await db.auth.getUser();
    const { data, error } = await db
      .from("platform_activities")
      .insert({
        title: payload.title,
        description: payload.description ?? "",
        city: payload.city,
        location: payload.location,
        starts_at: payload.starts_at,
        ends_at: payload.ends_at,
        capacity: payload.capacity ?? 0,
        allow_companions: payload.allow_companions === true,
        owner_id: identity.user?.id,
        status: "approved",
        reviewed_by: identity.user?.id,
        reviewed_at: new Date().toISOString(),
        submitted_at: new Date().toISOString(),
      })
      .select()
      .single();
    if (error) throw error;
    return data as T;
  }
  if (action === "review_activity") {
    const { data: identity } = await db.auth.getUser();
    const { data, error } = await db
      .from("platform_activities")
      .update({
        status: payload.status,
        moderation_reason: payload.reason ?? "",
        reviewed_by: identity.user?.id,
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", payload.activity_id)
      .select()
      .single();
    if (error) throw error;
    return data as T;
  }
  if (action === "delete_activity") {
    const { error } = await db.from("platform_activities").delete().eq("id", payload.activity_id);
    if (error) throw error;
    return { id: payload.activity_id } as T;
  }
  if (action === "list_alerts") {
    let query = db
      .from("platform_moderation_alerts")
      .select("id,activity_id,matched_word_ids,highest_severity,status,acknowledged_by,acknowledged_at,created_at")
      .order("created_at", { ascending: false })
      .limit(100);
    if (payload.status) query = query.eq("status", payload.status);
    const { data, error } = await query;
    if (error) throw error;
    const rows = (data ?? []) as PlatformAlert[];
    const activityIds = [...new Set(rows.map((r) => r.activity_id).filter(Boolean))];
    const titleById: Record<string, { title: string; city: string; status: string }> = {};
    if (activityIds.length) {
      const { data: acts } = await db
        .from("platform_activities")
        .select("id,title,city,status")
        .in("id", activityIds);
      for (const a of (acts ?? []) as Array<{ id: string; title: string; city: string; status: string }>) {
        titleById[a.id] = { title: a.title, city: a.city, status: a.status };
      }
    }
    return rows.map((r) => ({
      ...r,
      activity_title: titleById[r.activity_id]?.title ?? "",
      activity_city: titleById[r.activity_id]?.city ?? "",
      activity_status: titleById[r.activity_id]?.status ?? "",
    })) as T;
  }
  if (action === "update_alert_status") {
    const patch: Record<string, unknown> =
      payload.status === "open"
        ? { status: "open", acknowledged_by: null, acknowledged_at: null }
        : { status: payload.status, acknowledged_at: new Date().toISOString() };
    const { data, error } = await db
      .from("platform_moderation_alerts")
      .update(patch)
      .eq("id", payload.alert_id)
      .select()
      .single();
    if (error) throw error;
    return data as T;
  }
  if (action === "list_sensitive_words") {
    const { data, error } = await db
      .from("platform_sensitive_words")
      .select("id,phrase,severity,enabled,created_at,updated_at")
      .order("severity", { ascending: false });
    if (error) throw error;
    return (data ?? []) as T;
  }
  if (action === "upsert_sensitive_word") {
    const { data: identity } = await db.auth.getUser();
    const values = {
      phrase: payload.phrase,
      severity: payload.severity,
      enabled: payload.enabled !== false,
      created_by: identity.user?.id,
    };
    const query = payload.id
      ? db.from("platform_sensitive_words").update(values).eq("id", payload.id)
      : db.from("platform_sensitive_words").insert(values);
    const { data, error } = await query.select().single();
    if (error) throw error;
    return data as T;
  }
  if (action === "delete_sensitive_word") {
    const { error } = await db.from("platform_sensitive_words").delete().eq("id", payload.id);
    if (error) throw error;
    return { id: payload.id } as T;
  }
  if (action === "list_audit_logs") {
    let query = db
      .from("platform_admin_audit_logs")
      .select("id,actor_id,action,target_type,target_id,metadata,created_at")
      .order("created_at", { ascending: false })
      .limit(200);
    if (typeof payload.action === "string" && payload.action.trim()) query = query.eq("action", String(payload.action).trim());
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as T;
  }
  if (action === "list_ad_slots") {
    const { data, error } = await db.from("platform_ad_slots").select("id,key,label,enabled,created_at,updated_at").order("key");
    if (error) throw error;
    return (data ?? []) as T;
  }
  if (action === "upsert_ad_slot") {
    const values = { key: payload.key, label: payload.label, enabled: payload.enabled === true };
    const query = payload.id
      ? db.from("platform_ad_slots").update(values).eq("id", payload.id)
      : db.from("platform_ad_slots").insert(values);
    const { data, error } = await query.select().single();
    if (error) throw error;
    return data as T;
  }
  if (action === "delete_ad_slot") {
    const { error } = await db.from("platform_ad_slots").delete().eq("id", payload.id);
    if (error) throw error;
    return { id: payload.id } as T;
  }
  if (action === "list_ads") {
    const { data, error } = await db
      .from("platform_ads")
      .select("id,slot_id,title,body,image_url,target_url,starts_at,ends_at,enabled,created_at,updated_at")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw error;
    return (data ?? []) as T;
  }
  if (action === "upsert_ad") {
    const { data: identity } = await db.auth.getUser();
    const values = {
      slot_id: payload.slot_id,
      title: payload.title,
      body: payload.body ?? "",
      image_url: payload.image_url ?? "",
      target_url: payload.target_url ?? "",
      starts_at: payload.starts_at ?? null,
      ends_at: payload.ends_at ?? null,
      enabled: payload.enabled === true,
      created_by: identity.user?.id,
    };
    const query = payload.id
      ? db.from("platform_ads").update(values).eq("id", payload.id)
      : db.from("platform_ads").insert(values);
    const { data, error } = await query.select().single();
    if (error) throw error;
    return data as T;
  }
  if (action === "delete_ad") {
    const { error } = await db.from("platform_ads").delete().eq("id", payload.id);
    if (error) throw error;
    return { id: payload.id } as T;
  }
  if (action === "list_auth_providers") {
    const { data, error } = await db
      .from("platform_auth_providers")
      .select(
        "id,kind,provider_key,display_name,endpoint,template_id,signature,credential_ref,enabled,config,created_at,updated_at",
      )
      .order("kind");
    if (error) throw error;
    return (data ?? []) as T;
  }
  if (action === "upsert_auth_provider") {
    const { data: identity } = await db.auth.getUser();
    const values = { ...payload, created_by: identity.user?.id };
    const query = payload.id
      ? db.from("platform_auth_providers").update(values).eq("id", payload.id)
      : db.from("platform_auth_providers").insert(values);
    const { data, error } = await query.select().single();
    if (error) throw error;
    return data as T;
  }
  if (action === "delete_auth_provider") {
    const { error } = await db.from("platform_auth_providers").delete().eq("id", payload.id);
    if (error) throw error;
    return { id: payload.id } as T;
  }
  throw new Error("管理操作失败");
}
